import { CognitoUser } from '@aws-amplify/auth';
import { Amplify, Auth } from 'aws-amplify';
import { apiRootEndpoint, aws } from '../config';
import { useFlexAssessmentContext } from '../context/context';
import { FlexAssessmentContextAction } from '../types/flex-assessment-context-action';
import { NotificationType } from '../types/notification-type';
import { fetcher } from '../utils/fetcher';
import { STRINGS } from '../utils/strings';

const { userPoolId, userPoolWebClientId, region, identityPoolId } = aws;

Amplify.configure({
  Auth: {
    identityPoolId,
    region,
    userPoolId,
    userPoolWebClientId,
  },
});

export default function useAuthentication() {
  const { dispatch } = useFlexAssessmentContext();

  const signIn = async (
    username: string,
    password: string
  ): Promise<CognitoUser | Error> => {
    try {
      const user: CognitoUser = await Auth.signIn(
        username.toLowerCase(),
        password
      );
      dispatch({
        type: FlexAssessmentContextAction.setUserAuthenticationObject,
        payload: user,
      });

      return user;
    } catch (error) {
      dispatch({
        type: FlexAssessmentContextAction.showNotification,
        payload: {
          message: STRINGS.ERRORS.SOMETHING_WRONG,
          type: NotificationType.Error,
        },
      });
      throw Error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      await Auth.signOut();
    } catch (error) {
      dispatch({
        type: FlexAssessmentContextAction.showNotification,
        payload: {
          message: STRINGS.ERRORS.SOMETHING_WRONG_RETRY,
          type: NotificationType.Error,
        },
      });
    }
  };

  const requestPasswordVerificationCode = async (
    email: string
  ): Promise<any> => {
    try {
      return await fetcher(`${apiRootEndpoint}/forgotpassword/sendemail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
    } catch (error) {
      dispatch({
        type: FlexAssessmentContextAction.showNotification,
        payload: {
          message: STRINGS.ERRORS.SOMETHING_WRONG_RETRY,
          type: NotificationType.Error,
        },
      });
      throw Error;
    }
  };

  const resetPassword = async ({
    email,
    password,
    code,
  }: {
    email: string;
    password: string;
    code: string;
  }): Promise<any> => {
    try {
      return await fetcher(`${apiRootEndpoint}/forgotpassword/sendcode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, code }),
      });
    } catch (error) {
      dispatch({
        type: FlexAssessmentContextAction.showNotification,
        payload: {
          message: STRINGS.ERRORS.SOMETHING_WRONG_RETRY,
          type: NotificationType.Error,
        },
      });
      throw Error;
    }
  };

  return {
    signIn,
    signOut,
    requestPasswordVerificationCode,
    resetPassword,
  };
}
